import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useWebSocket } from "@/lib/ws";
import {
  Layers, Zap, Sparkles, Clock, Brain, TrendingUp, TrendingDown,
  Check, X, RefreshCw, ChevronRight, Bot, ShieldAlert,
} from "lucide-react";

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—");
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
const fmtNum = (n, d = 5) => (n === null || n === undefined ? "—" : Number(n).toFixed(d));
const fmtMoney = (n) => (n === null || n === undefined ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`);

const STATUS_STYLE = {
  waiting:               { c: "#a78bfa", bg: "rgba(167,139,250,0.12)", label: "WAITING", icon: <Clock className="w-3 h-3" /> },
  resolved:              { c: "#10b981", bg: "rgba(16,185,129,0.12)",  label: "RESOLVED", icon: <Check className="w-3 h-3" /> },
  resolved_both_closed:  { c: "#ef4444", bg: "rgba(239,68,68,0.12)",   label: "UNDECIDED · CLOSED", icon: <X className="w-3 h-3" /> },
  continued_hedged:      { c: "#f59e0b", bg: "rgba(245,158,11,0.12)",  label: "HEDGED (continued)", icon: <Layers className="w-3 h-3" /> },
  closed:                { c: "#6b7280", bg: "rgba(107,114,128,0.12)", label: "CLOSED", icon: <X className="w-3 h-3" /> },
};

const StatusPill = ({ status, testId }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.closed;
  return (
    <span data-testid={testId}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono font-bold border"
      style={{ color: s.c, borderColor: s.c, background: s.bg }}
    >
      {s.icon}{s.label}
    </span>
  );
};

const WinnerPill = ({ side }) => {
  if (!side || side === "none") return <span className="text-[10px] font-mono text-[#6b7280]">—</span>;
  const isBuy = side === "buy";
  const c = isBuy ? "#10b981" : "#ef4444";
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono font-bold border"
      style={{ color: c, borderColor: c, background: `${c}22` }}>
      {isBuy ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {side.toUpperCase()} WINS
    </span>
  );
};

const GroupCard = ({ group, expanded, onToggle }) => {
  const res = group.resolution;
  return (
    <div
      className="vx-card overflow-hidden transition-colors"
      data-testid={`straddle-group-${group.group_id}`}
    >
      <button
        onClick={onToggle}
        className="w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-[#1f2937]/40 transition-colors"
        data-testid={`straddle-toggle-${group.group_id}`}
      >
        <ChevronRight className={`w-4 h-4 text-[#6b7280] transition-transform ${expanded ? "rotate-90" : ""}`} />
        <div className="flex-1 grid grid-cols-1 md:grid-cols-5 gap-3 items-center">
          <div>
            <div className="font-mono font-bold text-sm">{group.symbol}</div>
            <div className="text-[10px] text-[#6b7280] font-mono">{group.timeframe} · {fmtDate(group.created_at)}</div>
          </div>
          <div>
            <div className="vx-label text-[9px]">Entry</div>
            <div className="font-mono text-xs">{fmtNum(group.entry_price)}</div>
          </div>
          <div>
            <div className="vx-label text-[9px]">Status</div>
            <StatusPill status={group.group_status} testId={`straddle-status-${group.group_id}`} />
          </div>
          <div>
            <div className="vx-label text-[9px]">AI verdict</div>
            <div className="flex items-center gap-2">
              <WinnerPill side={res?.winning_side} />
              {res?.confidence && (
                <span className="text-[10px] font-mono text-[#9ca3af]">
                  {res.confidence}% conf
                </span>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="vx-label text-[9px]">Aggregate P&amp;L</div>
            <div className={`font-mono text-sm font-bold ${group.aggregate_pnl >= 0 ? "text-[#10b981]" : "text-[#ef4444]"}`}>
              {fmtMoney(group.aggregate_pnl)}
            </div>
          </div>
        </div>
      </button>
      {expanded && (
        <div className="border-t border-[#1f2937] px-5 py-4 space-y-4 bg-[#0a0e1a]/40" data-testid={`straddle-detail-${group.group_id}`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(group.legs || []).map((leg) => (
              <div key={leg.id} className={`rounded border p-3 ${leg.direction === "BUY" ? "border-[#10b981]/40 bg-[#10b981]/5" : "border-[#ef4444]/40 bg-[#ef4444]/5"}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-mono font-bold">{leg.direction} · {leg.side?.toUpperCase()}</span>
                  <StatusPill status={leg.status || "closed"} />
                </div>
                <div className="grid grid-cols-3 gap-2 text-[10px] font-mono">
                  <div>
                    <div className="text-[#6b7280]">Entry</div>
                    <div className="text-white">{fmtNum(leg.entry_price)}</div>
                  </div>
                  <div>
                    <div className="text-[#6b7280]">SL</div>
                    <div className="text-[#ef4444]/80">{fmtNum(leg.sl)}</div>
                  </div>
                  <div>
                    <div className="text-[#6b7280]">TP</div>
                    <div className="text-[#10b981]/80">{fmtNum(leg.tp)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {res && (
            <div className="rounded border border-[#1f2937] bg-[#0a0e1a] p-3">
              <div className="flex items-center gap-2 mb-2">
                <Brain className="w-3.5 h-3.5 text-[#a78bfa]" />
                <span className="text-xs font-bold uppercase text-[#a78bfa] tracking-widest">AI Resolution</span>
                <span className="text-[10px] font-mono text-[#6b7280]">· {res.trigger_reason}</span>
              </div>
              <div className="text-sm text-[#d1d5db] leading-relaxed">{res.reasoning || "—"}</div>
              <div className="text-[10px] font-mono text-[#6b7280] mt-2">
                Resolved {fmtTime(res.resolved_at)} · Confidence {res.confidence || 0}%
              </div>
            </div>
          )}
          {group.config && (
            <div className="rounded border border-[#1f2937] bg-[#0a0e1a] p-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-[10px] font-mono">
              <div><span className="text-[#6b7280]">Lot each</span><div className="text-white">{group.config.lot_each_side}</div></div>
              <div><span className="text-[#6b7280]">Wait window</span><div className="text-white">{group.config.wait_window_seconds}s</div></div>
              <div><span className="text-[#6b7280]">AI threshold</span><div className="text-white">{group.config.ai_confidence_threshold}%</div></div>
              <div><span className="text-[#6b7280]">Multi-TF gate</span><div className="text-white">{group.config.require_multi_tf_alignment ? "ON" : "off"}</div></div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


export default function Straddles() {
  const [filter, setFilter] = useState("all");
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, h] = await Promise.all([
        api.get("/straddle/pending").catch(() => ({ data: { groups: [] } })),
        api.get("/straddle/history?days=30").catch(() => ({ data: { groups: [] } })),
      ]);
      setPending(p.data.groups || []);
      setHistory(h.data.groups || []);
    } catch (e) {
      toast.error("Failed to load straddles");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, [load]);

  useWebSocket((msg) => {
    if (["signal.straddle.created", "signal.straddle.resolved",
         "signal.straddle.undecided_closed", "signal.straddle.continued_hedged"].includes(msg.type)) {
      load();
    }
  });

  const visible = useMemo(() => {
    const all = [...pending, ...history.filter(h => !pending.find(p => p.group_id === h.group_id))];
    if (filter === "active") return all.filter(g => g.group_status === "waiting");
    if (filter === "resolved") return all.filter(g => g.group_status !== "waiting");
    return all;
  }, [pending, history, filter]);

  const stats = useMemo(() => {
    const resolvedGroups = history.filter(g => g.group_status !== "waiting");
    const wins = resolvedGroups.filter(g => (g.aggregate_pnl || 0) > 0).length;
    const total = resolvedGroups.length || 1;
    return {
      active: pending.length,
      resolved: resolvedGroups.length,
      win_rate: Math.round((wins / total) * 100),
      total_pnl: resolvedGroups.reduce((s, g) => s + (g.aggregate_pnl || 0), 0),
    };
  }, [pending, history]);

  return (
    <div className="space-y-6" data-testid="straddles-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="vx-label flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-[#a78bfa]" /> AI Arbitrage Lock
            <span className="text-[9px] font-mono uppercase tracking-wider text-[#6b7280] border border-[#1f2937] rounded px-1.5 py-0.5">Phase 7A</span>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tighter mt-1">Straddle-Lock Positions</h1>
          <p className="text-sm text-[#9ca3af] mt-2 max-w-2xl">
            Every signal opens BUY + SELL simultaneously. Backend AI evaluates market direction every 30s. Losing leg closes when confidence &gt; threshold. Winner runs with full trade management.
          </p>
        </div>
        <button onClick={load} data-testid="straddles-refresh"
          className="h-9 px-3 rounded-md border border-[#1f2937] hover:bg-[#1f2937] text-xs font-mono flex items-center gap-2">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="vx-card p-4">
          <div className="vx-label mb-1 flex items-center gap-1"><Clock className="w-3 h-3" /> Active</div>
          <div className="font-mono font-black text-3xl" data-testid="stat-active">{stats.active}</div>
        </div>
        <div className="vx-card p-4">
          <div className="vx-label mb-1 flex items-center gap-1"><Check className="w-3 h-3" /> Resolved (30d)</div>
          <div className="font-mono font-black text-3xl" data-testid="stat-resolved">{stats.resolved}</div>
        </div>
        <div className="vx-card p-4">
          <div className="vx-label mb-1 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Win rate</div>
          <div className="font-mono font-black text-3xl text-[#10b981]" data-testid="stat-winrate">{stats.win_rate}%</div>
        </div>
        <div className="vx-card p-4">
          <div className="vx-label mb-1 flex items-center gap-1"><Zap className="w-3 h-3" /> Total P&amp;L</div>
          <div className={`font-mono font-black text-3xl ${stats.total_pnl >= 0 ? "text-[#10b981]" : "text-[#ef4444]"}`} data-testid="stat-pnl">
            {fmtMoney(stats.total_pnl)}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {[["all", "All"], ["active", "Active"], ["resolved", "Resolved"]].map(([v, label]) => (
          <button key={v} onClick={() => setFilter(v)}
            data-testid={`filter-${v}`}
            className={`h-8 px-3 rounded-md text-xs font-mono border transition-colors ${
              filter === v
                ? "border-[#a78bfa] text-[#a78bfa] bg-[#a78bfa]/10"
                : "border-[#1f2937] text-[#9ca3af] hover:border-[#374151]"
            }`}>
            {label}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {visible.length === 0 ? (
          <div className="vx-card p-8 text-center text-sm text-[#6b7280] font-mono flex flex-col items-center gap-3" data-testid="straddles-empty">
            <Bot className="w-8 h-8 text-[#374151]" />
            <div>No straddle groups yet.</div>
            <div className="text-xs text-[#4b5563] max-w-md">
              Activate the "AI Arbitrage Lock" profile in /strategies to start generating dual-position pairs.
            </div>
          </div>
        ) : (
          visible.map((g) => (
            <GroupCard
              key={g.group_id}
              group={g}
              expanded={!!expanded[g.group_id]}
              onToggle={() => setExpanded((s) => ({ ...s, [g.group_id]: !s[g.group_id] }))}
            />
          ))
        )}
      </div>
    </div>
  );
}
